/**
 * importFamilyCards - Browser script to import parent contact data from CSV
 *
 * CSV columns: StudentID | Surname | Name | Class | Gender | ParentSurname | ParentName | ParentEmail | ParentCell | Address
 *
 * Usage:
 *   await window.importFamilyCards("paste\tcsv\there")
 *   await window.importFamilyCards("paste\tcsv\there", { dryRun: true })
 */
import { collection, getDocs, query, where, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';

/**
 * Parse tab-separated CSV text into rows
 */
function parseCSV(csvText) {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('CSV must have a header row and at least one data row');
  }

  const header = lines[0].split('\t').map(h => h.trim());
  console.log('CSV Headers:', header);

  // Map headers to expected fields (flexible matching)
  const headerMap = {};
  header.forEach((h, i) => {
    const lower = h.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (lower.includes('studentid') || lower === 'id') headerMap.studentId = i;
    else if (lower === 'surname' || lower === 'studentsurname' || lower === 'lastsurname') headerMap.surname = i;
    else if (lower === 'name' || lower === 'studentname' || lower === 'firstname') headerMap.name = i;
    else if (lower === 'class' || lower === 'grade' || lower === 'classname') headerMap.class = i;
    else if (lower === 'gender' || lower === 'sex') headerMap.gender = i;
    else if (lower.includes('parentsurname') || lower === 'parentlastname') headerMap.parentSurname = i;
    else if (lower.includes('parentname') || lower === 'parentfirstname') headerMap.parentName = i;
    else if (lower.includes('parentemail') || lower.includes('email')) headerMap.parentEmail = i;
    else if (lower.includes('parentcell') || lower.includes('phone') || lower.includes('cell') || lower.includes('mobile')) headerMap.parentPhone = i;
    else if (lower.includes('address')) headerMap.address = i;
  });

  console.log('Header mapping:', headerMap);

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split('\t').map(c => c.trim());
    if (cols.length < 3) continue; // skip empty/malformed rows

    rows.push({
      studentId: cols[headerMap.studentId] || '',
      surname: cols[headerMap.surname] || '',
      name: cols[headerMap.name] || '',
      class: cols[headerMap.class] || '',
      gender: cols[headerMap.gender] || '',
      parentSurname: cols[headerMap.parentSurname] || '',
      parentName: cols[headerMap.parentName] || '',
      parentEmail: (cols[headerMap.parentEmail] || '').toLowerCase().trim(),
      parentPhone: cols[headerMap.parentPhone] || '',
      address: cols[headerMap.address] || '',
    });
  }

  return rows;
}

/**
 * Match CSV rows to existing students in Firestore
 */
async function matchStudents(rows, schoolId) {
  const studentsSnap = await getDocs(collection(db, 'schools', schoolId, 'students'));
  const students = [];
  studentsSnap.forEach(d => students.push({ id: d.id, ...d.data() }));

  console.log(`Found ${students.length} students in school`);

  // Build lookup maps
  const byNumber = {};
  const byName = {};
  students.forEach(s => {
    if (s.studentNumber) {
      byNumber[String(s.studentNumber).trim()] = s;
    }
    const nameKey = `${(s.firstName || '').toLowerCase().trim()}_${(s.surname || s.lastName || '').toLowerCase().trim()}`;
    byName[nameKey] = s;
  });

  const matched = [];
  const unmatched = [];

  for (const row of rows) {
    // Try match by studentNumber first
    let student = byNumber[row.studentId];

    // Fallback: match by name
    if (!student) {
      const nameKey = `${row.name.toLowerCase()}_${row.surname.toLowerCase()}`;
      student = byName[nameKey];
    }

    if (student) {
      matched.push({ ...row, studentDocId: student.id, studentData: student });
    } else {
      unmatched.push(row);
    }
  }

  return { matched, unmatched };
}

/**
 * Group matched rows into family cards by email (primary) or phone (fallback)
 */
function groupIntoFamilies(matchedRows) {
  const byEmail = {};
  const byPhone = {};
  const noContact = [];

  for (const row of matchedRows) {
    if (row.parentEmail) {
      if (!byEmail[row.parentEmail]) {
        byEmail[row.parentEmail] = [];
      }
      byEmail[row.parentEmail].push(row);
    } else if (row.parentPhone) {
      if (!byPhone[row.parentPhone]) {
        byPhone[row.parentPhone] = [];
      }
      byPhone[row.parentPhone].push(row);
    } else {
      noContact.push(row);
    }
  }

  const families = [];

  // Email-grouped families
  for (const [email, rows] of Object.entries(byEmail)) {
    const studentIds = [...new Set(rows.map(r => r.studentDocId))];
    const first = rows[0];
    const contacts = [{
      firstName: first.parentName,
      surname: first.parentSurname,
      relationship: guessRelationship(first),
      email: email,
      phone: first.parentPhone || '',
    }];
    const contactEmails = [email];
    const address = first.address || '';

    families.push({ contacts, contactEmails, address, studentIds });
  }

  // Phone-grouped families (no email)
  for (const [phone, rows] of Object.entries(byPhone)) {
    const studentIds = [...new Set(rows.map(r => r.studentDocId))];
    const first = rows[0];
    const contacts = [{
      firstName: first.parentName,
      surname: first.parentSurname,
      relationship: guessRelationship(first),
      email: '',
      phone: phone,
    }];
    const address = first.address || '';

    families.push({ contacts, contactEmails: [], address, studentIds });
  }

  return { families, noContact };
}

/**
 * Guess parent relationship from name patterns
 */
function guessRelationship(row) {
  const name = (row.parentName || '').toLowerCase();
  if (name.includes('mrs') || name.includes('ms')) return 'mother';
  if (name.includes('mr')) return 'father';
  return 'guardian';
}

/**
 * Main import function
 */
export async function importFamilyCards(csvText, options = {}) {
  const { dryRun = false } = options;

  console.log('='.repeat(60));
  console.log(dryRun ? 'FAMILY CARDS IMPORT (DRY RUN)' : 'FAMILY CARDS IMPORT');
  console.log('='.repeat(60));

  const user = auth.currentUser;
  if (!user) {
    console.error('Not logged in');
    return;
  }

  // Get schoolId from localStorage
  const schoolContext = JSON.parse(localStorage.getItem('schoolContext') || '{}');
  const schoolId = schoolContext.schoolId;
  if (!schoolId) {
    console.error('No school context found. Make sure you are logged in and have a school selected.');
    return;
  }

  console.log(`School: ${schoolId}`);
  console.log(`User: ${user.email}`);

  // 1. Parse CSV
  const rows = parseCSV(csvText);
  console.log(`\nParsed ${rows.length} CSV rows`);

  // 2. Match to students
  const { matched, unmatched } = await matchStudents(rows, schoolId);
  console.log(`Matched: ${matched.length}, Unmatched: ${unmatched.length}`);

  if (unmatched.length > 0) {
    console.warn('Unmatched rows:');
    console.table(unmatched.map(r => ({
      studentId: r.studentId,
      name: `${r.name} ${r.surname}`,
      class: r.class,
    })));
  }

  // 3. Group into families
  const { families, noContact } = groupIntoFamilies(matched);
  console.log(`\nFamily cards to create: ${families.length}`);
  console.log(`Rows without any contact info: ${noContact.length}`);

  if (noContact.length > 0) {
    console.warn('Rows without contact info:');
    console.table(noContact.map(r => ({
      studentId: r.studentId,
      name: `${r.name} ${r.surname}`,
    })));
  }

  // Preview families
  console.log('\nFamily card preview:');
  families.forEach((f, i) => {
    console.log(`  ${i + 1}. ${f.contacts[0].firstName} ${f.contacts[0].surname} (${f.contacts[0].email || f.contacts[0].phone}) → ${f.studentIds.length} student(s)`);
  });

  if (dryRun) {
    console.log('\nDRY RUN - no changes made. Remove { dryRun: true } to execute.');
    return {
      parsed: rows.length,
      matched: matched.length,
      unmatched: unmatched.length,
      familyCards: families.length,
      noContact: noContact.length,
    };
  }

  // 4. Create family cards and update students
  console.log('\nCreating family cards...');
  const cardPath = `schools/${schoolId}/familyCards`;
  let created = 0;
  let studentsUpdated = 0;

  for (const family of families) {
    try {
      const cardData = {
        contacts: family.contacts,
        contactEmails: family.contactEmails,
        address: family.address,
        studentIds: family.studentIds,
        ownedBy: null,
        claimedAt: null,
        source: 'csv_import',
        importedBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const cardRef = await addDoc(collection(db, cardPath), cardData);
      created++;

      // Update each student with the familyCardId
      for (const studentId of family.studentIds) {
        try {
          const studentRef = doc(db, 'schools', schoolId, 'students', studentId);
          // Read current familyCardIds and append
          const studentSnap = await getDocs(query(
            collection(db, 'schools', schoolId, 'students'),
            where('__name__', '==', studentId)
          ));

          let currentCardIds = [];
          studentSnap.forEach(d => {
            currentCardIds = d.data().familyCardIds || [];
          });

          if (!currentCardIds.includes(cardRef.id)) {
            await updateDoc(studentRef, {
              familyCardIds: [...currentCardIds, cardRef.id],
              updatedAt: serverTimestamp(),
            });
            studentsUpdated++;
          }
        } catch (err) {
          console.warn(`  Failed to update student ${studentId}:`, err.message);
        }
      }
    } catch (err) {
      console.error(`  Failed to create card for ${family.contacts[0].email}:`, err.message);
    }
  }

  console.log('='.repeat(60));
  console.log('IMPORT COMPLETE');
  console.log(`  Family cards created: ${created}`);
  console.log(`  Students updated: ${studentsUpdated}`);
  console.log(`  Unmatched rows: ${unmatched.length}`);
  console.log(`  No contact info: ${noContact.length}`);
  console.log('='.repeat(60));

  return { created, studentsUpdated, unmatched: unmatched.length, noContact: noContact.length };
}
